import React, { useCallback, useEffect, useState } from 'react';
import MainText from '../../components/MainText';
import { Discount, NoteAdd, Visibility } from '@mui/icons-material';
import { addTab } from '../../tabs/tabsSlice';
import { useDispatch } from 'react-redux';
import { Box, Checkbox, Chip, IconButton, LinearProgress, TableCell, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import API from '../../services/api';
import { useAuth } from '../../auth/AuthProvider';
import CustomStepperInvoice from '../../components/InvoiceStepper/CustomStepperInvoice';
import { useLocation } from 'react-router-dom';
import { normalizeInvoicePaymentStatus, PAYMENT_STATUS } from '../../utils/paymentStatus';
import {
  mergeInvoiceLinesForDraft,
  normalizeCandidateOrders,
  normalizeInvoiceLineFormat,
} from '../../components/InvoiceStepper/invoiceReconciliation';

const formatPrice = (value) => {
  if (value === undefined || value === null) return 'Q -';

  const num = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(num)) return 'Q -';

  // Formato con separador de miles (,) y punto decimal (.)
  const formatted = new Intl.NumberFormat('es-GT', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);

  return `Q ${formatted}`;
};
const toPercent = (p) => {
  if (p == null) return 0;
  const num = typeof p === 'string' ? parseFloat(p) : p;
  if (Number.isNaN(num)) return 0;
  const clamped = Math.max(0, Math.min(num, 1));
  return Math.round(clamped * 100);
};
const normalizeProgress = (p) => {
  if (p == null) return 0;
  const num = Number(p);
  if (Number.isNaN(num)) return 0;
  if (num <= 1) return Math.round(num * 100);
  return Math.round(num);
};


// Se cargara desde /invoice/catalogs/create_invoice/
const FALLBACK_TYPES = [];

const userData = JSON.parse(localStorage.getItem("userData"));
const DRAFT_KEY = 'invoice:draft';
const DRAFT_INVOICE_FIELDS = [
  'date',
  'total',
  'invoice_adjustment',
  'invoiceType',
  'invoice_type',
  'invoice_type_id',
  'invoiceStatus',
  'number',
  'series',
  'paymentType',
  'supplier_id',
  'signedById',
  'notes',
  'invoice_detail',
  'invoice_bonus',
  'review_reference',
  'invoiceDocumentsPreview',
];

const readInvoiceDraft = () => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const sanitizeDraftInvoice = (draftInvoice) => {
  const sanitized = {};
  DRAFT_INVOICE_FIELDS.forEach((field) => {
    if (draftInvoice?.[field] !== undefined) {
      sanitized[field] = draftInvoice[field];
    }
  });
  if (typeof sanitized.invoiceDocumentsPreview === 'string' && sanitized.invoiceDocumentsPreview.startsWith('blob:')) {
    sanitized.invoiceDocumentsPreview = null;
  }
  sanitized.invoiceDocuments = null;
  return sanitized;
};

const normalizeInvoiceDraftLines = (rawInvoice) => {
  const safeInvoice = rawInvoice || {};
  const merged = mergeInvoiceLinesForDraft(
    safeInvoice?.invoice_detail || [],
    safeInvoice?.invoice_bonus || []
  );
  return {
    ...safeInvoice,
    invoice_detail: merged.invoice_detail,
    invoice_bonus: merged.invoice_bonus,
  };
};

const sanitizeDraftFlow = (draftFlow) => ({
  activeStep: Number(draftFlow?.activeStep || 0),
  completed: draftFlow?.completed || {},
  hasOrder: false,
  supplierId: draftFlow?.supplierId ?? null,
  selectionModel: Array.isArray(draftFlow?.selectionModel) ? draftFlow.selectionModel : [],
  productSearchTerm: draftFlow?.productSearchTerm || '',
});

const newEmptyInvoice = () => ({
  date: null,
  total: 0,
  invoiceType: 'FC',
  invoice_type: null,
  invoice_type_id: null,
  invoiceStatus: 'ISSF',
  number: '',
  series: '',
  paymentType: true,
  paymentStatus: PAYMENT_STATUS.PENDING,
  supplier_id: 0,
  signedById: null,
  invoice_detail: [],
  invoice_bonus: [],
  invoiceDocuments: null,
  invoiceDocumentsPreview: null,
});

export default function CreateInvoice() {
  const dispatch = useDispatch();
  const location = useLocation();
  const invoiceIdFromState = location.state?.invoiceId;
  const { company } = useAuth();
  // flujo local para la vista de factura (en lugar de Redux)
  const newEmptyFlow = () => ({
    activeStep: 0,
    completed: {},
    hasOrder: false,
    supplierId: null,
    selectionModel: [],
    productSearchTerm: '',
  });

  // Hidratar borrador desde localStorage (si existe)
  const [flow, setFlow] = useState(() => {
    try {
      if (invoiceIdFromState) return newEmptyFlow();
      const parsed = readInvoiceDraft();
      if (!parsed) return newEmptyFlow();
      const draftFlow = { ...newEmptyFlow(), ...sanitizeDraftFlow(parsed.flow) };
      return draftFlow;
    } catch {
      return newEmptyFlow();
    }
  });
  const values = flow;
  const invoiceFlow = flow;
  const setValues = (updater) => {
    setFlow((prev) => {
      const base = prev || newEmptyFlow();
      const next = typeof updater === 'function' ? updater(base) : updater;
      return { ...base, ...next };
    });
  };
  const [open, setOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const [catalogs, setCatalogs] = useState({ invoiceTypes: FALLBACK_TYPES, formats: [] });
  const [invoice, setInvoice] = useState(() => {
    try {
      if (invoiceIdFromState) return newEmptyInvoice();
      const parsed = readInvoiceDraft();
      if (!parsed) return newEmptyInvoice();
      const draftInvoice = normalizeInvoicePaymentStatus({
        ...newEmptyInvoice(),
        ...sanitizeDraftInvoice(parsed.invoice),
      }, parsed?.invoice?.paymentStatus);
      return normalizeInvoiceDraftLines(draftInvoice);
    } catch {
      return newEmptyInvoice();
    }
  });
  const productSearchTerm = values?.productSearchTerm || '';
  const handleProductSearchChange = useCallback((val) => {
    setValues(prev => ({ ...prev, productSearchTerm: val }));
  }, [setValues]);

  // Persistir borrador (invoice + flow) en localStorage
  useEffect(() => {
    if (invoiceIdFromState) return;
    try {
      const payload = {
        invoice: sanitizeDraftInvoice(invoice),
        flow: sanitizeDraftFlow(flow),
      };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
    } catch {
      // ignorar errores de storage
    }
  }, [flow, invoice, invoiceIdFromState]);

  useEffect(() => {
    dispatch(addTab({ label: 'Ingreso de factura', path: '/invoice/create-new' }));
  }, [dispatch]);

  // Cargar cat├ílogos una sola vez con cache por empresa (TTL 5m)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const cid = company?.id || 'global';
        const key = `catalogs:${cid}:create_invoice`;
        const cacheRaw = localStorage.getItem(key);
        const now = Date.now();
        if (cacheRaw) {
          try {
            const cached = JSON.parse(cacheRaw);
            if (cached?.expires && cached.expires > now && cached?.data) {
              if (mounted) setCatalogs({
                invoiceTypes: Array.isArray(cached.data.invoice_types) ? cached.data.invoice_types : FALLBACK_TYPES,
                formats: Array.isArray(cached.data.formats) ? cached.data.formats : []
              });
              return;
            }
          } catch { }
        }
        const { data } = await API.get('invoice/catalogs/create_invoice/');
        if (!mounted) return;
        const invoiceTypes = Array.isArray(data?.invoice_types)
          ? data.invoice_types
          : FALLBACK_TYPES;
        const formats = Array.isArray(data?.formats) ? data.formats : [];
        setCatalogs({ invoiceTypes, formats });
        try {
          localStorage.setItem(key, JSON.stringify({ data, expires: now + 5 * 60 * 1000 }));
        } catch { }
      } catch (e) {
        setCatalogs({ invoiceTypes: FALLBACK_TYPES, formats: [] });
      }
    })();
    return () => { mounted = false; };
  }, [company?.id]);

  // hidratar selecci├│n inicial desde flujo (solo cuando cambia selectionModel)
  useEffect(() => {
    const sel = invoiceFlow?.selectionModel;
    if (Array.isArray(sel)) {
      setSelectionModel((prev) => (prev === sel ? prev : sel));
    }
  }, [invoiceFlow?.selectionModel]);

  // Garantizar consistencia entre flujo.supplierId e invoice.supplier_id
  useEffect(() => {
    if (values?.supplierId && invoice?.supplier_id !== values.supplierId) {
      setInvoice(prev => ({ ...prev, supplier_id: values.supplierId }));
    }
  }, [values?.supplierId]);

  // Auto-cargar órdenes candidatas al seleccionar proveedor
  useEffect(() => {
    const shouldLoad = !!values?.supplierId;
    if (shouldLoad) {
      updateOrderList();
    } else {
      setOrders([]);
      setSelectionModel([]);
    }
  }, [values?.supplierId]);

  // Asegurar que al regresar al paso 0 se recarguen candidatas si aplica
  useEffect(() => {
    const atFirstStep = invoiceFlow?.activeStep === 0;
    if (atFirstStep && values?.supplierId) {
      if (!orders || orders.length === 0) {
        updateOrderList();
      }
      // Rehidratar selección desde el flujo si existe
      const sel = invoiceFlow?.selectionModel;
      if (Array.isArray(sel) && sel.length > 0) {
        setSelectionModel((prev) => (prev && prev.length ? prev : sel));
      }
    }
  }, [invoiceFlow?.activeStep]);

  useEffect(() => {
    if (!invoice?.signedById) {
      const ud = JSON.parse(localStorage.getItem('userData'));
      if (ud?.id) {
        setInvoice(prev => ({ ...prev, signedById: ud.id }));
      }
    }
  }, []);

  // Mantener invoice.invoice_type_id acorde al code invoice.invoiceType
  useEffect(() => {
    const code = String(invoice?.invoiceType || '').toUpperCase();
    if (!code) return;
    const nameByCode = code === 'FC' ? 'cambiaria' : code === 'FE' ? 'electr' : code === 'V' ? 'vale' : '';
    if (!nameByCode) return;
    const currentId = invoice?.invoice_type_id || null;
    const found = (catalogs.invoiceTypes || []).find(t => String(t?.name || '').toLowerCase().includes(nameByCode));
    const nextId = found?.id || null;
    if (nextId && nextId !== currentId) {
      setInvoice(prev => ({ ...prev, invoice_type_id: nextId }));
    }
  }, [invoice?.invoiceType, catalogs.invoiceTypes]);

  // Normalizar prformat_id en detalles y bonos usando cat├ílogos de formatos
  useEffect(() => {
    const fmts = catalogs.formats || [];
    if (!Array.isArray(invoice?.invoice_detail) && !Array.isArray(invoice?.invoice_bonus)) return;

    const mapFormatId = (v) => {
      if (!v) return null;
      if (typeof v === 'object') return v?.id || null;
      const s = String(v).trim().toLowerCase();
      const f = fmts.find(x => String(x?.name || '').toLowerCase() === s);
      return f?.id || null;
    };

    let changed = false;
    const normalizeLine = (line) => {
      const normalized = normalizeInvoiceLineFormat(line);
      const pid = normalized.prformat_id ?? mapFormatId(normalized.format_name ?? line?.prformat);
      const catalogFormat = fmts.find((item) => String(item?.id) === String(pid)) || null;
      const formatName = normalized.format_name ?? catalogFormat?.name ?? null;
      if (
        line?.prformat_id !== pid
        || line?.format_id !== pid
        || line?.format_name !== formatName
      ) {
        changed = true;
        return {
          ...line,
          prformat_id: pid,
          format_id: pid,
          format_name: formatName,
        };
      }
      return line;
    };

    const nextDetails = (invoice?.invoice_detail || []).map(normalizeLine);
    const nextBonuses = (invoice?.invoice_bonus || []).map(normalizeLine);
    if (changed) {
      setInvoice(prev => ({ ...prev, invoice_detail: nextDetails, invoice_bonus: nextBonuses }));
    }
  }, [catalogs.formats, invoice?.invoice_detail, invoice?.invoice_bonus]);

  const handleSupplierChange = (name, supplierId) => {
    setValues(prev => ({
      ...prev,
      supplierId,
      hasOrder: false
    }));

    setInvoice(prev => ({
      ...prev,
      supplier_id: supplierId
    }));
    if (values?.supplierId !== supplierId) {
      setOrders([]);
      setSelectionModel([]);
      setFlow(prev => ({ ...(prev || newEmptyFlow()), selectionModel: [] }));
    }
  };

  const fetchOrders = async (supplierId) => {
    if (!supplierId) return [];

    setLoading(true);
    try {
      const response2 = await API.get(`order-Av/get_orders_with_availability/`, {
        params: { supplier_id: supplierId }
      });
      const transformed = normalizeCandidateOrders(response2.data || []).map((order, idx) => {
        const baseId = order?.id ?? order?.orderId ?? order?.pk ?? idx;
        const safeId = Number.isFinite(Number(baseId)) ? Number(baseId) : `order-${idx}`;
        const supplierName = order?.supplier?.companyName
          || order?.order_details?.[0]?.product?.supplier?.companyName
          || 'Desconocido';
        const rawCreatedAt = order?.order_date ?? order?.createdAt ?? order?.dateTime ?? null;

        return {
          ...order,
          id: safeId,
          gridId: `order-${safeId}`,
          supplier_data: order?.supplier ?? null,
          supplier: supplierName,
          supplier_name: supplierName,
          createdAt: rawCreatedAt,
          createdAtDisplay: rawCreatedAt ? formatDateTime(rawCreatedAt) : '',
          bonusDiscount: order?.bonusDiscount ? formatCurrency(order.bonusDiscount) : 'Q 0.00',
          priceDiscount: order?.priceDiscount ? formatCurrency(order.priceDiscount) : 'Q 0.00',
          subTotal: order?.subTotal ? formatCurrency(order.subTotal) : 'Q 0.00',
          initial_total: order?.initial_total ? formatCurrency(order.initial_total) : 'Q 0.00',
          current_total: order?.current_total ? formatCurrency(order.current_total) : 'Q 0.00',
          orderStatus: order?.orderStatus || 'PE',
          progress: normalizeProgress(order?.progress),
          rawData: order || {}
        };
      });

      return transformed;
    } catch (error) {
      console.error("❌ Error obteniendo órdenes:", error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const updateOrderList = async () => {
    const supplierId = values?.supplierId || invoice?.supplier_id;
    if (supplierId) {
      const ordersData = await fetchOrders(supplierId);
      setOrders(ordersData);
    }
  };

  useEffect(() => {
    if (invoiceIdFromState) {
      const loadInvoice = async () => {
        try {
          const res = await API.get(`invoice/${invoiceIdFromState}/`);
          setInvoice(normalizeInvoiceDraftLines(res.data));
          setValues((prev) => ({
            ...prev,
            supplierId: res.data.supplier_id,
            hasOrder: false,
          }));
          const sel = res.data.order_ids || [];
          setSelectionModel(sel);
          setFlow(prev => ({ ...(prev || newEmptyFlow()), selectionModel: sel }));
          const ordersData = await fetchOrders(res.data.supplier_id);
          setOrders(ordersData);
        } catch (error) {
          console.error('Error cargando la factura', error);
        }
      };
      loadInvoice();
    }
  }, [invoiceIdFromState]);

  const formatDateTime = useCallback((dateString) => {
    if (!dateString) return 'Sin fecha';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Fecha inválida';
      return date.toLocaleString('es-GT', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (e) {
      console.error("Error formateando fecha:", dateString);
      return 'Fecha inválida';
    }
  }, []);


  const getStatusChip = useCallback((status) => {
    const statusMap = {
      'PE': { label: 'Pendiente de Entrega', color: 'primary' },
      'EP': { label: 'Entrega Parcial', color: 'warning' },
      'FI': { label: 'Finalizado', color: 'success' }
    };
    return statusMap[status] ? <Chip sx={{ marginBottom: '20px' }} label={statusMap[status].label} variant="filled" color={statusMap[status].color} /> : '-';
  }, []);
  // fuera del componente (o en un archivo constants)
  const INVOICE_TYPE = {
    FC: { label: "Factura cambiaria", color: "info" },
    FE: { label: "Factura electrónica", color: "success" },
    V: { label: "Vale", color: "warning" },
  };

  const invoiceTypeLabel = (code) =>
    INVOICE_TYPE[String(code || "").toUpperCase()]?.label || "ÔÇö";

  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' }).format(value || 0);
  }, []);

  const handleCheckboxChange = async () => {
    // Flujo actual: órdenes solo informativas/candidatas (sin selección manual obligatoria).
    setValues(prev => ({
      ...prev,
      hasOrder: false,
    }));
  };

  const handleEditClick = useCallback((order) => {
    setSelectedOrder(order.rawData);
    setOpen(true);
  }, []);

  const handleSelectionChange = (newSelection) => {
    setSelectionModel(newSelection);
    setFlow(prev => ({ ...(prev || newEmptyFlow()), selectionModel: newSelection }));
  };
  const processedOrders = (orders || []).map((order, index) => {
    if (!order) return null;
    const fallbackId = `order-${index}`;
    return {
      ...order,
      // DataGrid REQUIERE un id único
      id: order.id ?? order.gridId ?? fallbackId,
      // gridId personalizado
      gridId: order.gridId ?? order.id ?? fallbackId,
      // Asegurar propiedades base
      createdAt: order.createdAtDisplay || order.createdAt || '',
      supplier: order.supplier || '',
      subTotal: order.subTotal || 'Q 0.00',
      bonusDiscount: order.bonusDiscount || 'Q 0.00',
      priceDiscount: order.priceDiscount || 'Q 0.00',
      initial_total: order.initial_total || 'Q 0.00',
      current_total: order.current_total || 'Q 0.00',
      orderStatus: order.orderStatus || 'PE',
      progress: normalizeProgress(order.progress),
      rawData: order.rawData || order
    };
  }).filter(Boolean);


  const columns = [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'createdAt', headerName: 'Fecha creación', width: 150 },
    { field: 'supplier', headerName: 'Proveedor', width: 150 },
    { field: 'subTotal', headerName: 'Subtotal', width: 120 },
    { field: 'bonusDiscount', headerName: 'Desc. Bonif.', width: 120 },
    { field: 'priceDiscount', headerName: 'Desc. Precio', width: 120 },
    { field: 'initial_total', headerName: 'Total inicial', width: 120 },
    { field: 'current_total', headerName: 'Total restante', width: 120 },
    {
      field: 'orderStatus',
      headerName: 'Estado',
      width: 200,
      renderCell: (params) => getStatusChip(params.value)
    },
    {
      field: 'progress',
      headerName: 'Progreso',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ width: '100%', mt: '2px' }}>
          <Typography align='center' variant="body2" color="textSecondary">{params.value} %</Typography>
          <LinearProgress
            variant="determinate"
            value={params.value}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: params.value < 50 ? 'red' : 'green'
            }}
          />
        </Box>
      )
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex' }}>
          <Tooltip title="Ver/Editar">
            <IconButton
              color="primary"
              onClick={(event) => {
                event.stopPropagation(); // Evita que el clic afecte la selecci├│n
                handleEditClick(params.row);
              }}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
    ,
  ];


  const handleCheckbox = (productId, isChecked) => {
    setInvoice((prevInvoice) => {
      const updatedDetails = prevInvoice.invoice_detail.map((item) => {
        if (item.product_id === productId) {
          return {
            ...item,
            checked: isChecked,
            quantityRe: isChecked ? item.quantity : 0,
          };
        }
        return item;
      });
      return { ...prevInvoice, invoice_detail: updatedDetails };
    });
  };


  const handleQuantityChange = (productId, newValue) => {
    setInvoice((prevInvoice) => {
      const updatedDetails = prevInvoice.invoice_detail.map((item) => {
        if (item.product_id === productId) {
          const maxQuantity = item.quantity;
          return {
            ...item,
            quantityRe: newValue === '' ? '' : Math.min(newValue, maxQuantity),
          };
        }
        return item;
      });
      return { ...prevInvoice, invoice_detail: updatedDetails };
    });
  };

  const handleBonusCheckbox = (parentProductId, bonusIndex, isChecked) => {
    setInvoice(prevInvoice => {
      const updatedBonus = [...prevInvoice.invoice_bonus];
      let targetIndex = -1;
      if (parentProductId === -1) { targetIndex = bonusIndex; }
      else {
        const bonusesForProduct = updatedBonus.filter(b => b.product_id === parentProductId);
        const bonus = bonusesForProduct[bonusIndex];
        if (bonus) targetIndex = updatedBonus.findIndex(b => b === bonus || (b.product_id === parentProductId && b.id === bonus.id));
      }
      if (targetIndex >= 0) {
        updatedBonus[targetIndex] = { ...updatedBonus[targetIndex], checked: isChecked, quantityRe: isChecked ? updatedBonus[targetIndex].quantity : 0 };
      }
      return { ...prevInvoice, invoice_bonus: updatedBonus };
    });
  }; const handleBonusQuantityChange = (parentProductId, bonusIndex, newValue) => {
    setInvoice(prevInvoice => {
      const updatedBonus = [...prevInvoice.invoice_bonus];
      let targetIndex = -1; let maxQuantity = 0;
      if (parentProductId === -1) { targetIndex = bonusIndex; maxQuantity = updatedBonus[targetIndex]?.quantity || 0; }
      else {
        const bonusesForProduct = updatedBonus.filter(b => b.product_id === parentProductId);
        const bonus = bonusesForProduct[bonusIndex];
        if (bonus) { maxQuantity = bonus.quantity; targetIndex = updatedBonus.findIndex(b => b === bonus || (b.product_id === parentProductId && b.id === bonus.id)); }
      }
      if (targetIndex >= 0) {
        updatedBonus[targetIndex] = { ...updatedBonus[targetIndex], quantityRe: Math.min(newValue, maxQuantity) };
      }
      return { ...prevInvoice, invoice_bonus: updatedBonus };
    });
  };
  const renderExtras = (row, idxRow) => {
    const bonuses = (idxRow === 0) ? (invoice?.invoice_bonus || []) : []; if (bonuses.length === 0) return null; const safeText = (val) => (typeof val === 'string' ? val : val?.name || val?.description || val?.code || val?.prformat || val?.value || ''); return bonuses.map((bonus, idx) => {
      const bonusWithDefaults = {
        ...bonus,
        checked: bonus.checked ?? false,
        quantityRe: bonus.quantityRe ?? 0,
      };

      return (
        <TableRow
          key={`bonus-${row.product_id}-${bonus.id ?? idx}`}
          sx={{
            backgroundColor: '#f5f5f5',
            '& td': { py: '4px', fontSize: '0.8rem' },
          }}
        >
          <TableCell>
            <Box
              sx={{
                display: 'inline-block',
                px: 1,
                py: '2px',
                backgroundColor: '#EEF1F6',
                color: '#2B3E4D',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '0.8rem',
                textAlign: 'center',
              }}
            >
              {`${idx + 1}`}
            </Box>
          </TableCell>
          <TableCell align="left">
            <Checkbox
              checked={bonusWithDefaults.checked}
              onChange={(e) =>
                handleBonusCheckbox(-1, idx, e.target.checked)
              }
              color="primary"
              inputProps={{ 'aria-label': `Seleccionar ${safeText(bonus.description)}` }}
              size="small"
            />
          </TableCell>
          <TableCell>{safeText(bonus.code)}</TableCell>
          <TableCell>
            <Box display="flex" alignItems="center">
              <Discount color="success" fontSize="small" sx={{ mr: 1 }} />
              {safeText(bonus.description) || 'Producto promocional'}
            </Box>
          </TableCell>
          <TableCell align="right">{bonus.quantity}</TableCell>
          <TableCell align="right">{safeText(bonus.prformat)}</TableCell>
          <TableCell align="right">
            {!bonusWithDefaults.checked && (
              <TextField
                sx={{
                  width: '85px',
                  fontSize: '0.78rem',
                  borderRadius: '6px',
                  backgroundColor: '#f9f9f9',
                  boxShadow: '0 0 6px rgba(25, 118, 210, 0.4)',
                  transition: 'box-shadow 0.3s ease-in-out, background-color 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 0 8px rgba(25, 118, 210, 0.6)',
                  },
                  '& .MuiInputBase-root': {
                    padding: '3px 8px', // ÔåÉ altura m├ís c├│moda
                  },
                  '& input': {
                    fontSize: '0.78rem',
                    padding: '5px 6px',
                  },
                }}
                size="small"
                value={
                  bonusWithDefaults.quantityRe?.toString() === '0'
                    ? ''
                    : bonusWithDefaults.quantityRe?.toString() || ''
                }
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*$/.test(val)) {
                    handleBonusQuantityChange(-1, idx, val === '' ? '' : parseInt(val, 10)
                    );
                  }
                }}
                onBlur={(e) => {
                  const val = e.target.value;
                  const parsed = parseInt(val, 10);
                  const max = bonusWithDefaults.quantity;
                  if (!isNaN(parsed)) {
                    handleBonusQuantityChange(-1, idx, Math.min(parsed, max));
                  } else {
                    handleBonusQuantityChange(-1, idx, '');
                  }
                }}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
              />
            )}
          </TableCell>



          <TableCell />
        </TableRow>
      );
    });
  };



  const columnSummary = [
    {
      key: 'selection',
      label: 'Seleccionar',
      width: 50,
      render: (row) => (
        <Checkbox
          checked={!!row.checked}
          onChange={(e) => handleCheckbox(row.product_id, e.target.checked)}
          color="primary"
          inputProps={{ 'aria-label': `Seleccionar ${row.description}` }}
        />
      ),
    },
    { key: 'code', label: 'Codigo' },
    { key: 'description', label: 'Descripción' },
    { key: 'quantity', label: 'Cantidad', align: 'right' },
    { key: 'prformat', label: 'Formato', align: 'right' },
    {
      key: 'textField',
      label: 'Cantidad recibida',
      align: 'right',
      render: (row) => (
        !row.checked ? (
          <TextField
            // Usamos la variante 'outlined' que es est├índar y limpia
            variant="outlined"
            type="text"
            value={row.quantityRe?.toString() || ''}
            onChange={(e) => {
              const val = e.target.value;
              if (/^\d*$/.test(val)) {
                handleQuantityChange(row.product_id, val === '' ? '' : parseInt(val, 10));
              }
            }}
            onBlur={(e) => {
              const val = e.target.value;
              const parsed = parseInt(val, 10);
              if (!isNaN(parsed)) {
                const finalValue = Math.min(parsed, row.quantity);
                handleQuantityChange(row.product_id, finalValue);
              } else {
                handleQuantityChange(row.product_id, '');
              }
            }}
            // Tama├▒o "small" para compactar
            size="small"
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            sx={{
              // Ancho est├índar y ajustado para la celda
              width: '80px',

              // Simplificamos los estilos complejos, manteniendo la alineaci├│n a la derecha
              '& input': {
                textAlign: 'right',
                fontWeight: 500,
              },
              // Ajuste fino del padding interno si usas el estilo base de MUI
              '& .MuiInputBase-input': {
                padding: '8px 10px', // Estilo por defecto de small, ajustado ligeramente si es necesario
              },
            }}
          />

        ) : null
      ),
    }
    ,
    {
      key: 'finalPrice' || 'price',
      label: 'Precio Factura',
      align: 'right',
      render: (row) => formatPrice(row.price ?? row.finalPrice),
    },

  ];
  return (
    <>
      <MainText
        title={'Nueva factura'}
        subtitle={'Ingreso de información de facturas'}
        icon={NoteAdd}
      />

      <CustomStepperInvoice
        setValues={setValues}
        columns={columns}
        handleCheckboxChange={handleCheckboxChange}
        handleSupplierChange={handleSupplierChange}
        open={open}
        setOpen={setOpen}
        selectedOrder={selectedOrder}
        setSelectedOrder={setSelectedOrder}
        types={catalogs.invoiceTypes}
        updateOrderList={updateOrderList}
        values={values}
        orders={processedOrders}
        invoice={invoice}
        setInvoice={setInvoice}
        selectionModel={selectionModel}
        onSelectionModelChange={handleSelectionChange}
        setSelectionModel={setSelectionModel}
        columnsSummary={columnSummary}
        renderExtras={renderExtras}
        userData={userData}
        flow={flow}
        setFlowField={(field, value) =>
          setFlow(prev => ({ ...(prev || newEmptyFlow()), [field]: value }))
        }
        resetFlow={() => {
          setFlow(newEmptyFlow());
          setSelectionModel([]);
        }}
        initialSearchTerm={productSearchTerm}
        onSearchTermChange={handleProductSearchChange}
      />

    </>
  );
}




